import React, { type ClassType, type FunctionComponent, type JSX } from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { Loader } from './Loader.tsx';
import type { PromiseCompletion } from '../classes/PromiseCompletion.ts';

type ButtonColor = 'primary' | 'danger' | 'secondary' | 'warning' | 'info' | 'light';

export type ModalButtonOptions<T> = {
  type: ModalAction;
  color?: ButtonColor;
  title?: string;
  isDisabled?: boolean;
  onClick: (window: ModalWindow<T>) => void | Promise<void>;
};

export type ModalOptions<T> = {
  title?: string | React.ReactNode;
  width?: number | string;
  closeOnClickOutside?: boolean;
  closeByEscape?: boolean;
  dialog: (window: ModalWindow<T>) => JSX.Element;
  buttons?: (window: ModalWindow<T>) => ModalButtonOptions<T>[];
  onAppear?: (window: ModalWindow<T>) => void;
  onHide?: (window: ModalWindow<T>) => void;
};

export const ModalButtonType = {
  Ok: 'Ok',
  Cancel: 'Cancel',
  Close: 'Close',
  Save: 'Save',
  Add: 'Add',
  Select: 'Select',
  Update: 'Update',
  Remove: 'Remove',
  Refresh: 'Refresh'
} as const;

export type ModalButtonType = typeof ModalButtonType[keyof typeof ModalButtonType];

type ConfirmationOptions = {
  buttonTitle?: string;
  color?: ButtonColor;
  onDone?: () => Promise<void>;
};

type ModalAction = ModalButtonType | string;

export type ModalResult<T> = {
  button: ModalAction;
  result: T | null;
};

type ModalWindowProps<T> = ModalOptions<T> & {
  onClose: (action: ModalAction, result?: T) => void;
  closeOnClickOutside?: boolean;
};

type ModalPromise<T> = Promise<ModalResult<T>> & {
  close: (action?: ModalAction, result?: T) => void;
};

export type ModalDialogOptions<T> = {
  buttons?: ModalButtonOptions<T>[];
  loader?: PromiseCompletion;
  title?: string | JSX.Element;
  width?: number | string;
  className?: string;
  closeByEscape?: boolean;
  closeOnClickOutside?: boolean;
  onRenderFooter?: () => JSX.Element;
};

export interface IModalDialogContent<T> {
  getModalOptions: (window: ModalWindow<T>) => ModalDialogOptions<T>;
}

@observer
export class Modals extends React.Component<unknown> {
  render() {
    return (<>{modalService.renderModals()}</>);
  }
}

type ModalButtonProps<T> = ModalButtonOptions<T> & {
  window: ModalWindow<T>;
};

@observer
class ModalButton<T> extends React.Component<ModalButtonProps<T>> {
  @observable private _isWorking = false;

  constructor(props: ModalButtonProps<T>) {
    super(props);
    makeObservable(this);
  }

  render() {
    const { title, type, color, isDisabled } = this.props;
    let className = 'btn';
    if (isDisabled) {
      className += ' btn-disabled';
    }

    let buttonColor = color;
    if (!buttonColor) {
      if (type === ModalButtonType.Cancel || type === ModalButtonType.Close) {
        buttonColor = 'secondary';
      }
      if (type === ModalButtonType.Ok) {
        buttonColor = 'info';
      }
      if (type === ModalButtonType.Add || type === ModalButtonType.Save || type === ModalButtonType.Select || type === ModalButtonType.Update) {
        buttonColor = 'primary';
      }
      if (type === ModalButtonType.Remove) {
        buttonColor = 'danger';
      }
    }

    if (buttonColor) {
      className += ' btn-' + buttonColor;
    }
    return (
      <button className={className} disabled={isDisabled} onClick={this._onClick}>
        {title ?? type}
        {this._isWorking ? <Loader size="xsm" /> : null}
      </button>
    );
  }

  @action.bound
  private async _onClick() {
    const { window, onClick } = this.props;
    const result = onClick(window);
    if (result) {
      this._isWorking = true;
      try {
        await result;
      } finally {
        this._isWorking = false;
      }
    }
  }
}

const ModalContentLoader: React.FC = () => {
  return (
    <>
      <style>{`
        .modal-content-loader {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 200px;
          padding: 40px 20px;
          background: var(--bg-secondary);
          border-radius: 8px;
        }

        .modal-loader-spinner {
          position: relative;
          width: 60px;
          height: 60px;
          margin-bottom: 20px;
        }

        .modal-spinner-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border: 3px solid var(--border-color);
          border-top-color: var(--accent-color);
          border-radius: 50%;
          animation: modal-spin 1s linear infinite;
        }

        .modal-spinner-ring:nth-child(2) {
          border-top-color: var(--accent-hover);
          animation-duration: 1.5s;
          animation-direction: reverse;
          opacity: 0.6;
        }

        @keyframes modal-spin {
          to {
            transform: rotate(360deg);
          }
        }

        .modal-loader-text {
          color: var(--text-secondary);
          font-size: 14px;
          font-weight: 500;
          animation: modal-fade 1.5s ease-in-out infinite;
        }

        @keyframes modal-fade {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>

      <div className="modal-content-loader">
        <div className="modal-loader-spinner">
          <div className="modal-spinner-ring"></div>
          <div className="modal-spinner-ring"></div>
        </div>
        <div className="modal-loader-text">Loading content...</div>
      </div>
    </>
  );
};

@observer
export class ModalWindow<T> extends React.Component<ModalWindowProps<T>> {
  private _payload: T | null = null;
  @observable public contentRef: React.RefObject<IModalDialogContent<T> | null> = React.createRef();

  constructor(props: ModalWindowProps<T>) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this._onDocumentKeyDown);
    this.props.onAppear?.(this);
  }

  componentWillUnmount() {
    this.props.onHide?.(this);
    document.removeEventListener('keydown', this._onDocumentKeyDown);
  }

  render() {
    const { title, dialog, buttons, width } = this.props;
    const content = this.contentRef.current as IModalDialogContent<T>;

    const modalOptions = content?.getModalOptions?.(this);

    const titleToRender = modalOptions?.title ?? title;
    const maxWidth = modalOptions?.width ?? width;
    const widthToRender = typeof maxWidth === 'number' ? maxWidth + 'px' : maxWidth;
    const contentButtons = modalOptions?.buttons ?? [];
    const loader = modalOptions?.loader;
    const className = modalOptions?.className ? `modal ${modalOptions.className}` : 'modal';
    const buttonsToRender = (buttons?.(this) ?? []).concat(contentButtons);

    return (
      <div className={className} onClick={this._onBackgroundClick}>
        <div className="modal-content" style={{ maxWidth: widthToRender }}>
          <div className="modal-header">
            <div className="modal-title">{titleToRender}</div>
            <button className="modal-close" onClick={this._onCloseClick}>×</button>

          </div>
          <div className="modal-body">
            <React.Suspense fallback={<ModalContentLoader />}>
              {dialog(this)}
              {loader?.isPending ? <div className="modal-loader">
                <Loader size="sm" />
              </div> : null}
            </React.Suspense>
          </div>
          {!!buttonsToRender.length && <div className="modal-footer">
            {modalOptions?.onRenderFooter?.()}
            {buttonsToRender.map((b, index) => <ModalButton<T> key={`${b.type}${index}`} {...b} window={this} />)}
          </div>}
        </div>
      </div>
    );
  }

  @action.bound
  private _onBackgroundClick(event: React.MouseEvent) {
    const target = event.target as Element;
    const { closeOnClickOutside } = this.props;

    if (closeOnClickOutside === true && target.classList.contains('modal')) {
      this.props.onClose(ModalButtonType.Cancel, undefined);
    }
  }

  @action.bound
  private _onDocumentKeyDown(event: KeyboardEvent) {
    const VK_ESCAPE = 27;
    const { closeByEscape } = this.props;

    const content = this.contentRef.current as IModalDialogContent<T>;

    const modalOptions = content?.getModalOptions?.(this);
    const canClose = closeByEscape ?? modalOptions?.closeByEscape;

    if (event.keyCode === VK_ESCAPE && canClose !== false) {
      this.props.onClose(ModalButtonType.Cancel, undefined);
    }
  }

  @action.bound
  private _onCloseClick() {
    this.props.onClose(ModalButtonType.Cancel, undefined);
  }

  public close(button: ModalAction = ModalButtonType.Close, result?: T) {
    this.props.onClose(button, result || this._payload || void 0);
  }

  public getPayload(): T | null {
    return this._payload;
  }

  public setPayload(payload: T | null) {
    this._payload = payload;
  }
}

class ModalService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @observable private _modalData: ModalWindowProps<any>[] = [];

  constructor() {
    makeObservable(this);
  }

  public renderModals() {
    return this._modalData.map((m, index) => <ModalWindow key={'m' + index} {...m} />);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  public show<P extends {}, T>(dialog: ClassType<P, React.Component<P>, React.ComponentClass<P>> | FunctionComponent<P>, props?: P, options?: Omit<ModalDialogOptions<T>, 'dialog'>): ModalPromise<T> {
    return this.showModal<T>(
      Object.assign(
        {},
        {
          dialog: (window: ModalWindow<T>) => {
            const content = React.createElement(dialog, Object.assign({}, props, { ref: window.contentRef }));
            return content;
          }
        },
        options
      ) as ModalOptions<T>
    );
  }

  public showModal<T>(options: ModalOptions<T>): ModalPromise<T> {
    let data: ModalWindowProps<T> | null = null;
    const result = new Promise<ModalResult<T>>((resolve) => {
      data = Object.assign(options, {
        onClose: (button: ModalAction, result?: T) => {
          resolve({
            button: button,
            result: result || null
          });
          const modalIndex = this._modalData.findIndex(d => d === data);
          if (modalIndex !== -1) {
            this._modalData.splice(modalIndex, 1);
          }
          if (!this._modalData.length) {
            document.body.classList.remove('modal-open');
          }
        }
      });
      if (!this._modalData.length) {
        document.body.classList.add('modal-open');
      }
      this._modalData.push(data);
      data = this._modalData[this._modalData.length - 1];
    });

    return Object.assign(result, {
      close: (action?: ModalAction, result?: T) => data?.onClose(action || ModalButtonType.Ok, result)
    });
  }

  public async showConfirmation(content: string | JSX.Element, title: string, options?: ConfirmationOptions) {
    const result = await modalService.showModal({
      title: title ?? 'Confirmation',
      width: '500px',
      dialog: () => <>{content}</>,
      buttons: (dialog) => {
        return [{
          title: 'Cancel',
          type: ModalButtonType.Cancel,
          onClick: () => dialog.close(ModalButtonType.Cancel)
        }, {
          title: options?.buttonTitle ?? 'Ok',
          color: options?.color || 'primary',
          type: ModalButtonType.Ok,
          onClick: async () => {
            await options?.onDone?.();
            dialog.close(ModalButtonType.Ok);
          }
        }];
      }
    });

    return result.button === ModalButtonType.Ok;
  }

  public async showInformation(content: string | JSX.Element, title?: string) {
    const result = await modalService.showModal({
      title: title || 'Information',
      width: '500px',
      dialog: () => <>{content}</>,
      buttons: (dialog) => {
        return [
          {
            title: 'Ok',
            type: ModalButtonType.Ok,
            onClick: async () => {
              dialog.close(ModalButtonType.Ok);
            }
          }
        ];
      }
    });

    return result.button === ModalButtonType.Ok;
  }
  
  public async showError(content: string | JSX.Element, title?: string) {
    const result = await modalService.showModal({
      title: title || 'Error',
      width: '500px',
      dialog: () => <>{content}</>,
      buttons: (dialog) => {
        return [
          {
            title: 'Ok',
            type: ModalButtonType.Ok,
            onClick: async () => {
              dialog.close(ModalButtonType.Ok);
            }
          }
        ];
      }
    });

    return result.button === ModalButtonType.Ok;
  }
}

export const modalService = new ModalService();